/**
 * Admin Seed Users Page
 * Visit /seed-users to trigger database seeding
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, Database } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SeedUsersPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSeed = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await fetch(`${API}/api/wyldecrest-auth/seed-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await res.json();
      
      if (data.success) {
        setResult(data);
      } else {
        setError(data.detail || 'Seeding failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className="text-center">
          <Database className="w-12 h-12 mx-auto mb-4 text-[#1F7A78]" />
          <CardTitle className="text-2xl">Seed Database Users</CardTitle>
          <p className="text-slate-500 mt-2">
            Click below to create default Wyldecrest portal users
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={handleSeed} 
            disabled={loading}
            className="w-full bg-[#1F7A78] hover:bg-[#16615f] text-white h-12 text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Seeding...
              </>
            ) : (
              <>
                <Database className="w-5 h-5 mr-2" />
                Seed Users Now
              </>
            )}
          </Button>

          {result && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                <CheckCircle className="w-5 h-5" />
                Success!
              </div>
              <p className="text-green-600 text-sm mb-2">{result.message}</p>
              {result.created?.length > 0 && (
                <div className="text-sm">
                  <p className="font-medium text-green-700">Created:</p>
                  <ul className="list-disc list-inside text-green-600">
                    {result.created.map(email => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}
              {result.existing?.length > 0 && (
                <div className="text-sm mt-2">
                  <p className="font-medium text-slate-600">Already existed:</p>
                  <ul className="list-disc list-inside text-slate-500">
                    {result.existing.map(email => (
                      <li key={email}>{email}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <XCircle className="w-5 h-5" />
                <span className="font-semibold">Error:</span> {error}
              </div>
            </div>
          )}

          <div className="text-center text-sm text-slate-400 pt-4 border-t">
            This will create: dean@lhs.com, admin@wyldecrest.co.uk,<br/>
            helpdesk@wyldecrest.co.uk, ops@wyldecrest.co.uk
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
